import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Typography, Box, Stack, IconButton,
  Collapse, List, ListItem, ListItemText, Chip, CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AttachFile as AttachIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../utils/functions/api';
import { DevWinBadge } from '../DevPageBadge';

const TYPE_LABELS = {
  delivery_note: 'ใบส่งงาน / ใบส่งสินค้า',
  billing_note:  'ใบวางบิล / ใบแจ้งหนี้',
  tax_invoice:   'ใบกำกับภาษี',
};

function fmt(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
}

export default function CreateNextDocModal({ open, onClose, parentDoc, targetType, onConfirm, loading }) {
  const today = dayjs().format('YYYY-MM-DD');
  const [form, setForm] = useState({
    date: today,
    due_date: today,
    customer_po_number: '',
    reference_text: '',
    notes: '',
    terms_conditions: '',
    payment_terms: '',
    po_document_url: '',
  });
  const [showItems, setShowItems] = useState(false);
  const [useCustomRef, setUseCustomRef] = useState(false);
  const [uploadingPO, setUploadingPO] = useState(false);
  const [poError, setPoError] = useState('');
  const poInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        date: dayjs().format('YYYY-MM-DD'),
        due_date: dayjs().format('YYYY-MM-DD'),
        customer_po_number: '',
        reference_text: '',
        notes: parentDoc?.notes || '',
        terms_conditions: parentDoc?.terms_conditions || '',
        payment_terms: parentDoc?.payment_terms || '',
        po_document_url: '',
      });
      setShowItems(false);
      setUseCustomRef(false);
      setPoError('');
    }
  }, [open, parentDoc]);

  const autoRef = parentDoc
    ? form.customer_po_number
      ? `${parentDoc.document_number}_PO_${form.customer_po_number}`
      : parentDoc.document_number
    : '';

  const handleUploadPO = async (file) => {
    if (!file) return;
    setUploadingPO(true);
    setPoError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/upload/file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setForm((p) => ({ ...p, po_document_url: data?.url || '' }));
    } catch {
      setPoError('อัพโหลดไม่สำเร็จ — ไฟล์ต้องไม่เกิน 1MB');
    } finally {
      setUploadingPO(false);
    }
  };

  const handleSubmit = () => {
    if (targetType === 'delivery_note' && !form.po_document_url) {
      setPoError('กรุณาแนบใบ PO จากลูกค้าก่อนสร้างใบส่งงาน');
      return;
    }
    onConfirm({
      ...form,
      reference_text: useCustomRef ? form.reference_text : autoRef,
    });
  };

  if (!parentDoc) return null;

  return (
    <>
    <DevWinBadge num={143} name="CreateNextDocModal" open={open} />
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontWeight={700}>สร้าง{TYPE_LABELS[targetType]}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Parent doc info */}
        <Box sx={{ bgcolor: '#f0f7ff', border: '1px solid #c7dff7', borderRadius: 2, p: 2, mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
            📋 ข้อมูลจาก {parentDoc.document_number}
          </Typography>
          <Stack spacing={0.25}>
            <Typography variant="body2"><b>ลูกค้า:</b> {parentDoc.snapshot?.customer_name}</Typography>
            <Typography variant="body2"><b>โปรเจกต์:</b> {parentDoc.snapshot?.project_name}</Typography>
            <Typography variant="body2">
              <b>ยอดรวม:</b> ฿{fmt(parentDoc.total)} ({parentDoc.items?.length || 0} รายการ)
            </Typography>
          </Stack>

          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              endIcon={showItems ? <CollapseIcon /> : <ExpandIcon />}
              onClick={() => setShowItems((p) => !p)}
              sx={{ p: 0, fontSize: 12 }}
            >
              {showItems ? 'ซ่อนรายการ' : 'ดูรายการ'}
            </Button>
            <Collapse in={showItems}>
              <List dense disablePadding sx={{ mt: 1 }}>
                {(parentDoc.items || []).map((it, i) => (
                  <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={<Typography variant="caption">{i + 1}. {it.description}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{it.quantity} {it.unit} × {fmt(it.unit_price)} = {fmt(it.amount)}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        </Box>

        <Stack spacing={2}>
          {/* PO attachment — required for delivery_note */}
          {targetType === 'delivery_note' && (
            <Box sx={{ border: '1.5px solid', borderColor: poError ? '#ef4444' : '#e2e8f0', borderRadius: 2, p: 2 }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color={poError ? 'error' : 'text.primary'}>
                  แนบใบ PO จากลูกค้า
                </Typography>
                <Chip label="บังคับ" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
              </Stack>
              {form.po_document_url ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Chip
                    label="ดูใบ PO"
                    size="small"
                    color="success"
                    icon={<OpenIcon sx={{ fontSize: 13 }} />}
                    onClick={() => window.open(form.po_document_url, '_blank')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="ลบ"
                    size="small"
                    icon={<DeleteIcon sx={{ fontSize: 13 }} />}
                    onClick={() => setForm((p) => ({ ...p, po_document_url: '' }))}
                    sx={{ cursor: 'pointer' }}
                  />
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={uploadingPO ? <CircularProgress size={13} /> : <AttachIcon sx={{ fontSize: 14 }} />}
                    onClick={() => poInputRef.current?.click()}
                    disabled={uploadingPO}
                    sx={{ borderRadius: 2, fontSize: 12 }}
                  >
                    {uploadingPO ? 'กำลังอัพโหลด...' : 'เลือกไฟล์ PO'}
                  </Button>
                  <Typography variant="caption" color="text.disabled">PDF, JPG, PNG</Typography>
                </Stack>
              )}
              <input
                ref={poInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={(e) => handleUploadPO(e.target.files?.[0])}
              />
              {poError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{poError}</Typography>
              )}
            </Box>
          )}

          {/* PO number */}
          {(targetType === 'delivery_note' || targetType === 'billing_note' || targetType === 'tax_invoice') && (
            <TextField
              label="เลข PO ลูกค้า"
              size="small"
              value={form.customer_po_number}
              onChange={(e) => setForm((p) => ({ ...p, customer_po_number: e.target.value }))}
              placeholder="เช่น PO_106/2569"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}

          {/* Date fields */}
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="วันที่ *"
              type="date"
              size="small"
              value={form.date}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="ครบกำหนด"
              type="date"
              size="small"
              value={form.due_date}
              onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>

          {/* Reference */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>เลขที่อ้างอิง</Typography>
              <Button size="small" sx={{ fontSize: 11, p: 0 }} onClick={() => setUseCustomRef((p) => !p)}>
                {useCustomRef ? 'ใช้อัตโนมัติ' : 'แก้ไขเอง'}
              </Button>
            </Stack>
            <TextField
              fullWidth
              size="small"
              value={useCustomRef ? form.reference_text : autoRef}
              onChange={(e) => setForm((p) => ({ ...p, reference_text: e.target.value }))}
              disabled={!useCustomRef}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>

          {/* Notes */}
          <TextField
            fullWidth
            label="หมายเหตุ"
            multiline
            rows={2}
            size="small"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* Terms & conditions */}
          <TextField
            fullWidth
            label="เงื่อนไขการเสนอราคา"
            multiline
            rows={2}
            size="small"
            value={form.terms_conditions}
            onChange={(e) => setForm((p) => ({ ...p, terms_conditions: e.target.value }))}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* Payment terms */}
          <TextField
            fullWidth
            label="เงื่อนไขการชำระเงิน"
            size="small"
            value={form.payment_terms}
            onChange={(e) => setForm((p) => ({ ...p, payment_terms: e.target.value }))}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>ยกเลิก</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || uploadingPO}
          sx={{ borderRadius: 2 }}
        >
          {loading ? 'กำลังสร้าง...' : '✅ สร้าง'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
